import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, Subject, catchError, finalize, takeUntil, tap } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanySalesOrderShipment } from 'src/app/core/models/sales/sales-order-shipment.interface';
import {
  ShipmentStatus,
  getShipmentStatusLabelKey,
  normalizeShipmentStatus,
} from 'src/app/core/models/sales/shipment-status.type';
import { SalesOrderShipmentService } from 'src/app/core/services/sales-order-shipment.service';

interface ShipmentRowView {
  id: string;
  number: string;
  shipmentDate: string;
  lineCount: number;
  totalBaseQuantity: number;
  status: ShipmentStatus;
  statusLabelKey: string;
  statusBadgeClass: string;
  // Precomputed status-tinted classes for the leading truck-icon block. Bound from the template
  // via direct property access — no method calls (AGENTS.md rule 9 / Angular template rule).
  iconBgClass: string;
  iconTextClass: string;
  canMarkShipped: boolean;
  // Precomputed per-row mutation flags so the template never invokes component methods
  // (AGENTS.md rule 9 / Angular template rule). `markShipped` rebuilds the row to flip these.
  isMutating: boolean;
  errorKey: string | null;
}

// Tailwind status pill classes precomputed on the row so the template never invokes
// component methods for visual state (AGENTS.md rule 9 / Angular template rule).
const SHIPMENT_STATUS_BADGE_CLASSES: Readonly<Record<ShipmentStatus, string>> = {
  Packed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
  Shipped: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
};

// Status-tinted icon-block surface classes mirroring the Finance tab's pattern. Packed
// shipments read as in-progress (amber), Shipped reads as success (green).
const SHIPMENT_STATUS_ICON_CLASSES: Readonly<
  Record<ShipmentStatus, { bg: string; text: string }>
> = {
  Packed: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
  },
  Shipped: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
  },
};

@Component({
  selector: 'app-sales-order-shipments-tab',
  standalone: true,
  imports: [CommonModule, CoreModule, TranslateModule],
  templateUrl: './sales-order-shipments-tab.component.html',
  styleUrl: './sales-order-shipments-tab.component.scss',
})
export class SalesOrderShipmentsTabComponent implements OnChanges, OnDestroy {
  private readonly shipmentService = inject(SalesOrderShipmentService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public salesOrder: CompanySalesOrderDetails | null = null;
  @Input({ required: true }) public unitsOfMeasure: UnitOfMeasureLookup[] = [];
  @Input() public disabled = false;
  // Resolved human-readable customer name forwarded from the parent overview to the
  // create-shipment modal. The modal falls back to the order's customerNumber when empty.
  @Input() public customerName = '';
  // Bumped by the parent overview whenever the underlying sales order is re-fetched (e.g.
  // after a quick-action shipment creation on the Overview tab). The change handler clears
  // the dedupe key so `loadShipmentsIfNeeded` re-fires, keeping the list in sync.
  @Input() public refreshToken = 0;

  @Output() public readonly shipmentCreated = new EventEmitter<void>();
  @Output() public readonly shipmentUpdated = new EventEmitter<void>();
  // The "+ NEW" button bubbles to the parent overview so a single create-shipment modal
  // instance mounted at the page-content level handles every entry point (Overview quick
  // action AND in-tab CTA). Eliminates the dual-mount visual drift the previous design
  // had between the Overview and Shipments entry points.
  @Output() public readonly createShipmentRequested = new EventEmitter<void>();

  public readonly shipmentRows$ = new BehaviorSubject<ShipmentRowView[]>([]);

  public canCreateShipment = false;
  public isLoading = false;
  public hasLoadError = false;
  // Per-row mutation guards so a double-click cannot fire two PATCHes at once. Keyed by row id
  // so concurrent ops on different rows do not block each other. The Set is the source of truth
  // for the in-flight guard; ShipmentRowView.isMutating mirrors it for template binding.
  public readonly mutatingRowIds = new Set<string>();

  private readonly unsubscribe$ = new Subject<void>();
  private lastLoadedOrderId = '';

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['salesOrder'] || changes['companyId']) {
      this.recomputeFlags();
      this.loadShipmentsIfNeeded();
    }
    // Parent-driven invalidation: bumping `refreshToken` forces a refetch even when the
    // sales order identity has not changed (e.g. a quick-action shipment creation that re-
    // fetches the same order). Skip the firstChange pass so we do not double-load on mount.
    if (
      changes['refreshToken'] &&
      !changes['refreshToken'].firstChange &&
      changes['refreshToken'].currentValue !== changes['refreshToken'].previousValue
    ) {
      this.lastLoadedOrderId = '';
      this.loadShipmentsIfNeeded();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * In-tab "+ NEW" button handler — bubbles up to the parent overview, which owns the
   * single create-shipment modal mount. The parent re-fetches the order on success and
   * bumps `refreshToken` so this tab's shipment list reloads via `ngOnChanges`.
   */
  public requestCreateShipment(): void {
    if (!this.canCreateShipment || this.disabled) {
      return;
    }
    this.createShipmentRequested.emit();
  }

  public retry(): void {
    this.lastLoadedOrderId = '';
    this.loadShipmentsIfNeeded();
  }

  /**
   * Per-row Packed -> Shipped transition. Idempotent at the row level: the button is hidden
   * once the row already shows Shipped, and `mutatingRowIds` blocks rapid double-clicks while
   * the PATCH is in flight. Patches the in-memory row from the response instead of refetching
   * the whole list to keep the surface snappy.
   */
  public markShipped(row: ShipmentRowView): void {
    if (this.disabled || !this.companyId) {
      return;
    }
    if (!row.canMarkShipped || this.mutatingRowIds.has(row.id)) {
      return;
    }

    this.mutatingRowIds.add(row.id);
    // Flip the precomputed flags on the in-memory row so the template reflects the
    // in-flight state without calling component methods from bindings.
    this.patchRowFlags(row.id, { isMutating: true, errorKey: null });

    this.shipmentService
      .markShipped(this.companyId, row.id)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((updated) => {
          this.patchRow(updated, { isMutating: false, errorKey: null });
          this.shipmentUpdated.emit();
        }),
        catchError(() => {
          this.patchRowFlags(row.id, {
            isMutating: false,
            errorKey: 'COMPONENTS.SALES_ORDERS.SHIPMENTS.MARK_SHIPPED_FAILED',
          });
          return EMPTY;
        }),
        finalize(() => {
          this.mutatingRowIds.delete(row.id);
        }),
      )
      .subscribe();
  }

  public isMutating(rowId: string): boolean {
    return this.mutatingRowIds.has(rowId);
  }

  /** Test helper that reads the precomputed VM error key. Not used from the template. */
  public rowErrorKey(rowId: string): string {
    return this.shipmentRows$.getValue().find((row) => row.id === rowId)?.errorKey ?? '';
  }

  private recomputeFlags(): void {
    const status = this.salesOrder?.status ?? null;
    // Cancelled is the only terminal state that blocks shipment creation; a missing
    // status means the order has not loaded yet so the CTA stays disabled.
    this.canCreateShipment = status !== null && status !== 'Cancelled';
  }

  private loadShipmentsIfNeeded(): void {
    const order = this.salesOrder;
    if (!order || !this.companyId) {
      this.shipmentRows$.next([]);
      return;
    }

    if (order.id === this.lastLoadedOrderId) {
      return;
    }

    this.lastLoadedOrderId = order.id;
    this.isLoading = true;
    this.hasLoadError = false;

    this.shipmentService
      .getShipments(this.companyId, order.id)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((shipments) => this.shipmentRows$.next(shipments.map((shipment) => this.mapRow(shipment)))),
        catchError(() => {
          this.hasLoadError = true;
          this.shipmentRows$.next([]);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private patchRow(
    updated: CompanySalesOrderShipment,
    flags: { isMutating: boolean; errorKey: string | null },
  ): void {
    const next = this.mapRow(updated, flags);
    const current = this.shipmentRows$.getValue();
    const replaced = current.map((row) => (row.id === next.id ? next : row));
    this.shipmentRows$.next(replaced);
  }

  /**
   * Patch only the per-row mutation flags without re-fetching the underlying shipment.
   * Used to flip `isMutating`/`errorKey` from the markShipped pipeline so the template
   * reads precomputed VM fields instead of invoking component methods.
   */
  private patchRowFlags(
    rowId: string,
    flags: { isMutating: boolean; errorKey: string | null },
  ): void {
    const current = this.shipmentRows$.getValue();
    const replaced = current.map((row) =>
      row.id === rowId ? { ...row, isMutating: flags.isMutating, errorKey: flags.errorKey } : row,
    );
    this.shipmentRows$.next(replaced);
  }

  private mapRow(
    shipment: CompanySalesOrderShipment,
    flags: { isMutating: boolean; errorKey: string | null } = { isMutating: false, errorKey: null },
  ): ShipmentRowView {
    const totalBaseQuantity = shipment.lines.reduce(
      (sum, line) => sum + (Number.isFinite(line.baseQuantity) ? Number(line.baseQuantity) : 0),
      0,
    );
    // baseQuantity is the only field safe to sum across FIFO slices — quantity mirrors the request line.
    const distinctRequestLines = new Set(shipment.lines.map((line) => line.salesOrderLineId)).size;
    const status = normalizeShipmentStatus(shipment.status);
    const iconClasses = SHIPMENT_STATUS_ICON_CLASSES[status];

    return {
      id: shipment.id,
      number: shipment.number,
      shipmentDate: shipment.shipmentDate,
      lineCount: distinctRequestLines || shipment.lines.length,
      totalBaseQuantity,
      status,
      statusLabelKey: getShipmentStatusLabelKey(status),
      statusBadgeClass: SHIPMENT_STATUS_BADGE_CLASSES[status],
      iconBgClass: iconClasses.bg,
      iconTextClass: iconClasses.text,
      canMarkShipped: status === 'Packed',
      isMutating: flags.isMutating,
      errorKey: flags.errorKey,
    };
  }
}
